import React, { Component } from 'react';
import { MdRouter, MdLocationOn } from 'react-icons/md';
import { useMapData } from '@app/contexts/mapData.context';

type DeviceType = 'controller' | 'site';

interface MapItem {
  id: string;
  displayText?: string;
  status: number;
  type: DeviceType;
  location: {
    city: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
  };
  link?: string;
}

interface BubbleMapProps {
  controllers: MapItem[];
  sites: MapItem[];
  googleMapsLoaded?: boolean;
  googleMapsLoading?: boolean;
  googleMapsError?: string | null;
}

interface BubbleMapState {
  mapLoaded: boolean;
  loadError: string | null;
  tooltip: {
    content: MapItem[];
    position: { x: number; y: number };
  } | null;
  isHoveringTooltip: boolean;
}

const statusColors: Record<number, string> = {
  0: "#000000", // Unknown - Black
  1: "#ff0000", // Down - Red
  2: "#FBC02D", // Warning - Yellow
  3: "#45B649", // Good - Green
  4: "#808080"  // Missing - Gray
};

// Helper function to create SVG marker with shape only (no text)
const createMarker = (
  color: string,
  size: number,
  shape: 'circle' | 'square' = 'circle'
): string => {
  const diameter = size * 2;
  const radius = size;
  
  const shapeElement = shape === 'square' 
    ? `<rect x="1" y="1" width="${diameter - 2}" height="${diameter - 2}" fill="${color}" stroke="#ffffff" stroke-width="2" fill-opacity="0.8"/>`
    : `<circle cx="${radius}" cy="${radius}" r="${radius - 1}" fill="${color}" stroke="#ffffff" stroke-width="2" fill-opacity="0.8"/>`;
  
  return `
    <svg width="${diameter}" height="${diameter}" viewBox="0 0 ${diameter} ${diameter}" xmlns="http://www.w3.org/2000/svg">
      ${shapeElement}
    </svg>
  `;
};


// Convert SVG to data URL
const svgToDataUrl = (svgString: string): string => {
  return `data:image/svg+xml;base64,${btoa(svgString)}`;
};

// First, create a functional wrapper component that uses the hook
const GoogleMapWithBubblesDashboardWrapper: React.FC<BubbleMapProps> = (props) => {
  const { state, loadGoogleMaps } = useMapData();
  
  React.useEffect(() => {
    // Only load Google Maps if not already loaded or loading
    if (!state.googleMapsLoaded && !state.googleMapsLoading) {
      loadGoogleMaps();
    }
  }, [state.googleMapsLoaded, state.googleMapsLoading, loadGoogleMaps]);

  // Pass the cached Google Maps loading state to the class component
  return (
    <GoogleMapWithBubblesDashboard 
      {...props} 
      googleMapsLoaded={state.googleMapsLoaded}
      googleMapsLoading={state.googleMapsLoading}
      googleMapsError={state.googleMapsError}
    />
  );
};

class GoogleMapWithBubblesDashboard extends Component<BubbleMapProps, BubbleMapState> {
  private mapRef = React.createRef<HTMLDivElement>();
  private map: any = null;
  private markers: any[] = [];
  private infoWindow: any = null;
  private markerClusterer: any = null;
  private updateMarkersTimeout: NodeJS.Timeout | null = null;

  state: BubbleMapState = {
    mapLoaded: false,
    loadError: null,
    tooltip: null,
    isHoveringTooltip: false,
  };

  async componentDidMount() {
    // Check if Google Maps is already loaded from cache
    if (this.props.googleMapsLoaded) {
      this.initializeMap();
      this.setState({ mapLoaded: true });
    }
  }

  componentDidUpdate(prevProps: BubbleMapProps) {
    // Handle Google Maps loading state changes
    if (!prevProps.googleMapsLoaded && this.props.googleMapsLoaded && !this.map) {
      this.initializeMap();
      this.setState({ mapLoaded: true });
    }

    // Handle Google Maps loading errors
    if (!prevProps.googleMapsError && this.props.googleMapsError) {
      this.setState({ loadError: this.props.googleMapsError });
    }

    // Update markers when data changes (with debounce to prevent flashing)
    if (this.state.mapLoaded && this.map &&
        (prevProps.controllers !== this.props.controllers || 
         prevProps.sites !== this.props.sites)) {
      
      // Clear existing timeout
      if (this.updateMarkersTimeout) {
        clearTimeout(this.updateMarkersTimeout);
      }
      
      // Debounce marker updates to prevent rapid flashing
      this.updateMarkersTimeout = setTimeout(() => {
        this.updateMarkers();
      }, 100);
    }
  }

  componentWillUnmount() {
    // Clean up timeout
    if (this.updateMarkersTimeout) {
      clearTimeout(this.updateMarkersTimeout);
    }
  }

  initializeMap = () => {
    if (!this.mapRef.current) {
      setTimeout(() => this.initializeMap(), 100);
      return;
    }

    if (!(window as any).google) {
      return;
    }

    const google = (window as any).google;
    
    try {
      // Create map with enhanced styling
      this.map = new google.maps.Map(this.mapRef.current, {
        zoom: 10,
        center: { lat: 30, lng: 0 },
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: false,
        scaleControl: false,
        rotateControl: false,
        panControl: false,
        gestureHandling: 'auto',
        disableDefaultUI: true,
        keyboardShortcuts: false,
        clickableIcons: false,
        styles: [
          {
            "elementType": "geometry",
            "stylers": [{ "color": "#1d2c4d" }]
          },
          {
            "elementType": "labels.text.fill",
            "stylers": [{ "color": "#8ec3b9" }]
          },
          {
            "elementType": "labels.text.stroke",
            "stylers": [{ "color": "#1a3646" }]
          },
          {
            "featureType": "administrative.country",
            "elementType": "geometry.stroke",
            "stylers": [{ "color": "#4b6878" }]
          },
          {
            "featureType": "water",
            "elementType": "geometry",
            "stylers": [{ "color": "#0e1626" }]
          },
          {
            "featureType": "road",
            "elementType": "geometry",
            "stylers": [{ "color": "#38414e" }]
          },
          {
            "featureType": "road",
            "elementType": "geometry.stroke",
            "stylers": [{ "color": "#212a37" }]
          },
          {
            "featureType": "road",
            "elementType": "labels.text.fill",
            "stylers": [{ "color": "#9ca5b3" }]
          },
          {
            "featureType": "road.highway",
            "elementType": "geometry",
            "stylers": [{ "color": "#746855" }]
          },
          {
            "featureType": "road.highway",
            "elementType": "geometry.stroke",
            "stylers": [{ "color": "#1f2835" }]
          },
          {
            "featureType": "road.highway",
            "elementType": "labels.text.fill",
            "stylers": [{ "color": "#f3d19c" }]
          },
          {
            "featureType": "road.highway",
            "elementType": "labels",
            "stylers": [{ "visibility": "off" }]
          },
          {
            "featureType": "road",
            "elementType": "labels.text",
            "stylers": [{ "visibility": "off" }]
          },
          {
            "featureType": "road",
            "elementType": "labels.icon",
            "stylers": [{ "visibility": "off" }]
          },
          // Hide all POI (Point of Interest) markers
          {
            "featureType": "poi",
            "stylers": [{ "visibility": "off" }]
          },
          {
            "featureType": "poi.business",
            "stylers": [{ "visibility": "off" }]
          },
          {
            "featureType": "poi.attraction",
            "stylers": [{ "visibility": "off" }]
          },
          {
            "featureType": "poi.government",
            "stylers": [{ "visibility": "off" }]
          },
          {
            "featureType": "poi.medical",
            "stylers": [{ "visibility": "off" }]
          },
          {
            "featureType": "poi.park",
            "stylers": [{ "visibility": "off" }]
          },
          {
            "featureType": "poi.place_of_worship",
            "stylers": [{ "visibility": "off" }]
          },
          {
            "featureType": "poi.school",
            "stylers": [{ "visibility": "off" }]
          },
          {
            "featureType": "poi.sports_complex",
            "stylers": [{ "visibility": "off" }]
          },
          {
            "featureType": "transit",
            "stylers": [{ "visibility": "off" }]
          },
          {
            "featureType": "transit.line",
            "stylers": [{ "visibility": "off" }]
          },
          {
            "featureType": "transit.station",
            "stylers": [{ "visibility": "off" }]
          }
        ]
      });

      // Create info window for tooltips
      this.infoWindow = new google.maps.InfoWindow();

      // Add click listener to map to close info window when clicking outside
      this.map.addListener('click', () => {
        this.infoWindow.close();
      });

      // Add markers
      this.updateMarkers();
      
    } catch (error) {
      console.error('Error initializing map:', error);
    }
  };

  private groupDevicesByCoordinates(): MapItem[][] {
    const { controllers, sites } = this.props;
    

    
    // The data is already in the correct format, just ensure it has the required properties
    const allDevices = [...controllers, ...sites];

    
    const groups = new Map<string, MapItem[]>();

    allDevices.forEach(item => {
      // Check if coordinates exist
      if (!item.location?.coordinates?.latitude || !item.location?.coordinates?.longitude) {

        return;
      }
      
      const key = `${item.location.coordinates.latitude},${item.location.coordinates.longitude}`;

      const group = groups.get(key) || [];
      groups.set(key, [...group, item]);
    });

    const result = Array.from(groups.values());

    return result;
  }

  updateMarkers = () => {
    if (!this.map || !(window as any).google) return;

    const google = (window as any).google;

    // Clear existing markers
    this.markers.forEach(marker => marker.setMap(null));
    this.markers = [];

    // Group devices by coordinates
    const deviceGroups = this.groupDevicesByCoordinates();

    if (deviceGroups.length === 0) {
      return;
    }

    // Create enhanced markers for each group
    deviceGroups.forEach((group, index) => {
      const firstDevice = group[0];

      
      // Check if we have valid coordinates
      if (!firstDevice.location?.coordinates?.latitude || !firstDevice.location?.coordinates?.longitude) {

        return;
      }
      
      const controllers = group.filter(d => d.type === 'controller');
      const sites = group.filter(d => d.type === 'site');
      

      const getMarkerColor = (devices: MapItem[]) => {
        const statuses = devices.map(device => device.status || 0);
        
        // Priority order: Unknown (0), Down (1), Warning (2), Missing (4), Good (3)
        if (statuses.includes(0)) return statusColors[0]; // Unknown - Black
        if (statuses.includes(1)) return statusColors[1]; // Down - Red  
        if (statuses.includes(2)) return statusColors[2]; // Warning - Yellow
        if (statuses.includes(4)) return statusColors[4]; // Missing - Gray
        if (statuses.includes(3)) return statusColors[3]; // Good - Green
        
        // Default to Unknown if no valid status found
        return statusColors[0];
      };

      const markerColor = getMarkerColor(group);

      // Generate tooltip title showing device names instead of location
      const getMarkerTitle = (devices: MapItem[]) => {
        if (devices.length === 1) {
          // Single device: show just the device name
          return devices[0].displayText || devices[0].id;
        } else {
          // Multiple devices: show first device name + count
          const firstName = devices[0].displayText || devices[0].id;
          return `${firstName} (+${devices.length - 1} more)`;
        }
      };

      const iconSize = Math.min(10 + group.length * 2, 20); // Increased base size from 6 to 10
      const offsetDistance = 0.0001; // Small offset for separating markers
      
      // Create separate markers for mixed locations
      if (controllers.length > 0 && sites.length > 0) {
        // Create controller marker (offset left)
        const controllerIcon = createMarker(markerColor, iconSize, 'square');
        const controllerMarker = new google.maps.Marker({
          position: { 
            lat: firstDevice.location.coordinates.latitude, 
            lng: firstDevice.location.coordinates.longitude - offsetDistance
          },
          map: this.map,
          title: `Controllers: ${controllers.map(c => c.displayText || c.id).join(', ')}`,
          icon: {
            url: svgToDataUrl(controllerIcon),
            scaledSize: new google.maps.Size(iconSize * 2, iconSize * 2),
            anchor: new google.maps.Point(iconSize, iconSize),
          },
          zIndex: group.length
        });

        // Create site marker (offset right)
        const siteIcon = createMarker(markerColor, iconSize, 'circle');
        const siteMarker = new google.maps.Marker({
          position: { 
            lat: firstDevice.location.coordinates.latitude, 
            lng: firstDevice.location.coordinates.longitude + offsetDistance
          },
          map: this.map,
          title: `Sites: ${sites.map(s => s.displayText || s.id).join(', ')}`,
          icon: {
            url: svgToDataUrl(siteIcon),
            scaledSize: new google.maps.Size(iconSize * 2, iconSize * 2),
            anchor: new google.maps.Point(iconSize, iconSize),
          },
          zIndex: group.length
        });

        // Add click listeners for mixed markers
        controllerMarker.addListener('click', () => {
          const content = this.createAdvancedInfoWindowContent(
            firstDevice.location.city, 
            controllers, 
            []
          );
          this.infoWindow.setContent(content);
          this.infoWindow.open(this.map, controllerMarker);
        });

        siteMarker.addListener('click', () => {
          const content = this.createAdvancedInfoWindowContent(
            firstDevice.location.city, 
            [], 
            sites
          );
          this.infoWindow.setContent(content);
          this.infoWindow.open(this.map, siteMarker);
        });

        this.markers.push(controllerMarker, siteMarker);
        return; // Skip the single marker creation below
      }
      
      // Single type markers (controllers only or sites only)
      const svgIcon = controllers.length > 0 
        ? createMarker(markerColor, iconSize, 'square')
        : createMarker(markerColor, iconSize, 'circle');
      
      const marker = new google.maps.Marker({
        position: { 
          lat: firstDevice.location.coordinates.latitude, 
          lng: firstDevice.location.coordinates.longitude 
        },
        map: this.map,
        title: getMarkerTitle(group),
        icon: {
          url: svgToDataUrl(svgIcon),
          scaledSize: new google.maps.Size(iconSize * 2, iconSize * 2),
          anchor: new google.maps.Point(iconSize, iconSize),
        },
        zIndex: group.length // Higher priority for larger groups
      });



      // Add click listener for detailed info window
      marker.addListener('click', () => {
        const content = this.createAdvancedInfoWindowContent(
          firstDevice.location.city, 
          controllers, 
          sites
        );
        this.infoWindow.setContent(content);
        this.infoWindow.open(this.map, marker);
      });

      // Add hover effects for single-type markers only
      marker.addListener('mouseover', () => {
        const hoverSize = Math.min(12 + group.length * 2, 24); // Increased hover size
        const hoverIcon = controllers.length > 0 
          ? createMarker(markerColor, hoverSize, 'square')
          : createMarker(markerColor, hoverSize, 'circle');
        
        marker.setIcon({
          url: svgToDataUrl(hoverIcon),
          scaledSize: new google.maps.Size(hoverSize * 2, hoverSize * 2),
          anchor: new google.maps.Point(hoverSize, hoverSize),
        });
      });

      marker.addListener('mouseout', () => {
        marker.setIcon({
          url: svgToDataUrl(svgIcon),
          scaledSize: new google.maps.Size(iconSize * 2, iconSize * 2),
          anchor: new google.maps.Point(iconSize, iconSize),
        });
      });

      this.markers.push(marker);
    });



    // Auto-fit bounds to show all markers
    if (this.markers.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      this.markers.forEach(marker => {
        bounds.extend(marker.getPosition());
      });
      this.map.fitBounds(bounds);
      
      // Don't zoom in too much for single markers
      google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
        if (this.map.getZoom() > 15) {
          this.map.setZoom(15);
        }
      });
      

    } else {

    }
  };

  createAdvancedInfoWindowContent = (city: string, controllers: MapItem[], sites: MapItem[]) => {
    const controllersList = controllers.map(controller => 
      `<div style="display: flex; align-items: center; margin: 3px 0; padding: 2px;">
        <span style="margin-right: 8px;">🔧</span>
        <span style="width: 8px; height: 8px; border-radius: 50%; background-color: ${statusColors[controller.status]}; margin-right: 8px;"></span>
        <span>${controller.displayText || controller.id}</span>
      </div>`
    ).join('');

    const sitesList = sites.map(site => 
      `<div style="display: flex; align-items: center; margin: 3px 0; padding: 2px;">
        <span style="margin-right: 8px;">📍</span>
        <span style="width: 8px; height: 8px; border-radius: 50%; background-color: ${statusColors[site.status]}; margin-right: 8px;"></span>
        <span>${site.displayText || site.id}</span>
      </div>`
    ).join('');

    return `
      <div style="color: #222; font-family: 'Segoe UI', Arial, sans-serif; min-width: 250px; max-width: 350px;">
        <div style="font-size: 16px; font-weight: bold; margin-bottom: 10px; color: #1a365d; border-bottom: 2px solid #e2e8f0; padding-bottom: 5px;">
          📍 ${city}
        </div>
        
        ${controllers.length > 0 ? `
          <div style="margin-bottom: 8px;">
            <div style="font-weight: 600; color: #2d3748; margin-bottom: 4px;">
              Controllers (${controllers.length})
            </div>
            ${controllersList}
          </div>
        ` : ''}
        
        ${sites.length > 0 ? `
          <div style="margin-bottom: 8px;">
            <div style="font-weight: 600; color: #2d3748; margin-bottom: 4px;">
              Sites (${sites.length})
            </div>
            ${sitesList}
          </div>
        ` : ''}
        
        <div style="margin-top: 10px; padding-top: 8px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #718096;">
          Total: ${controllers.length + sites.length} devices
        </div>
      </div>
    `;
  };

  render() {
    const { mapLoaded, loadError } = this.state;

    if (loadError) {
      return (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          backgroundColor: '#1a202c',
          color: '#e2e8f0',
          fontSize: '16px',
          borderRadius: '8px',
          border: '1px solid #2d3748'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🗺️</div>
          <div>{loadError}</div>
          <div style={{ fontSize: '12px', marginTop: '8px', opacity: 0.7 }}>
            Please check your Google Maps API configuration
          </div>
        </div>
      );
    }

    if (!mapLoaded) {
      return (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          backgroundColor: '#1a202c',
          color: '#e2e8f0',
          fontSize: '16px',
          borderRadius: '8px',
          border: '1px solid #2d3748'
        }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #4a5568',
            borderTop: '4px solid #63b3ed',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }}></div>
          <div>Loading Google Maps...</div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      );
    }

    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <div
          ref={this.mapRef}
          style={{
            width: '100%',
            height: '551px',
            backgroundColor: '#808080',
            borderRadius: '8px',
            overflow: 'hidden'
          }}
        />
        <style>{`
          /* Hide Terms link - not required by Google ToS */
          .gm-style a[href*="terms"] {
            display: none !important;
          }
          /* Hide Keyboard shortcuts link - not required */
          .gm-style a[href*="keyboard"] {
            display: none !important;
          }
          /* Remove background from attribution text */
          .gm-style .gmnoprint {
            background: transparent !important;
            background-color: transparent !important;
          }
          .gm-style .gm-style-cc {
            background: transparent !important;
            background-color: transparent !important;
          }
          /* Target the specific attribution container */
          .gm-style .gm-style-cc > div {
            background: transparent !important;
            background-color: transparent !important;
          }
          /* Remove any box shadow or borders */
          .gm-style .gm-style-cc * {
            background: transparent !important;
            background-color: transparent !important;
            box-shadow: none !important;
          }
        `}</style>
      </div>
    );
  }
}

export default GoogleMapWithBubblesDashboardWrapper; 